import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { Observable, map } from 'rxjs';

import { ApisStatusEnum, FinnairApisRequirementKeyEnum, FinnairCompletionStatus } from '@fcom/dapi/api/models';
import { ButtonComponent, ButtonSize, ButtonTheme } from '@fcom/ui-components';

import { FinnairPassengerItemWithApis } from '../../../check-in/interface/check-in.interface';

interface PassengerWithDetails extends FinnairPassengerItemWithApis {
  swiped: boolean;
  started: boolean;
  verifiersEnabled: boolean;
  completed: boolean;
  isInAgentReval: boolean;
}

interface OpenFormEvent {
  passengerId: string;
  triggerElement: ButtonComponent;
}

@Component({
  selector: 'fin-apis-passenger-checkin',
  templateUrl: 'apis-passenger-checkin.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ApisPassengerCheckinComponent implements OnInit {
  readonly ButtonSize = ButtonSize;
  readonly ButtonTheme = ButtonTheme;
  readonly FinnairCompletionStatus = FinnairCompletionStatus;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  @Input() passenger$: Observable<FinnairPassengerItemWithApis>;
  @Input() hideEditButton = false;
  @Input() verifiersEnabled = false;

  @Output() openForm: EventEmitter<OpenFormEvent> = new EventEmitter<OpenFormEvent>();

  passengerWithDetails$: Observable<PassengerWithDetails>;

  ngOnInit(): void {
    this.passengerWithDetails$ = this.passenger$.pipe(
      map((passenger) => {
        const isAllowedToAcceptCheckIn = passenger.eligibilities.some((flight) => flight.isAllowedToAcceptCheckIn);
        const informationsCompleteOrNotRequired =
          passenger.apisCpr?.apisStatus === ApisStatusEnum.COMPLETE ||
          passenger.apisCpr?.apisStatus === ApisStatusEnum.NOT_REQUIRED ||
          isAllowedToAcceptCheckIn;
        const completed =
          passenger.apisCpr?.requirements?.every(
            (requirement) => requirement.required && requirement.status === FinnairCompletionStatus.COMPLETED
          ) && informationsCompleteOrNotRequired;

        const requirements = passenger.apisCpr?.requirements ?? [];
        const visaAndPassportRequirements = requirements.filter((r) =>
          [FinnairApisRequirementKeyEnum.PASSPORT, FinnairApisRequirementKeyEnum.VISA].includes(r.key)
        );

        return {
          ...passenger,
          swiped: visaAndPassportRequirements.length > 0 && visaAndPassportRequirements.every((r) => r.swiped === true),
          started: requirements.some((r) => r.required && r.status === FinnairCompletionStatus.COMPLETED),
          verifiersEnabled: this.verifiersEnabled,
          completed,
          isInAgentReval: passenger.isInAgentReval,
        };
      })
    );
  }
}
